import imageCarouselDefinitions from './imageCarousels.js';
import { setParallaxTranslation } from './parallaxMotion.js';

const imageCarouselModule = {
  update: null,
  init(definitions = imageCarouselDefinitions) {
    const container = document.querySelector('.pages-container');
    const strip = container?.querySelector('.pages-inner');
    if (!strip) return;
    const desktop = matchMedia('(min-width: 768px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const entries = [];

    for (const definition of definitions) {
      const page = Array.from(strip.querySelectorAll(':scope > .page'))
        .find(element => element.dataset.id === String(definition.page));
      if (!page || !definition.images?.length) continue;
      const carousel = document.createElement('section');
      carousel.className = `image-carousel image-carousel--${definition.style === 'stack' ? 'stack' : 'pagination'}`;
      if (definition.arrowColor === 'light') carousel.classList.add('image-carousel--arrows-light');
      else if (definition.arrowColor === 'dark') carousel.classList.add('image-carousel--arrows-dark');
      carousel.setAttribute('aria-label', definition.label || 'Image carousel');
      carousel.tabIndex = 0;
      carousel.style.setProperty('--carousel-aspect-ratio', definition.aspectRatio || '4 / 3');

      const stage = document.createElement('div');
      stage.className = 'image-carousel__stage';
      const isStack = definition.style === 'stack';
      const slides = Array.from({ length: isStack ? 3 : definition.images.length }, () => {
        const image = document.createElement('img');
        image.className = 'image-carousel__slide';
        image.loading = isStack ? 'eager' : 'lazy';
        image.draggable = false;
        stage.append(image);
        return image;
      });

      const controls = document.createElement('div');
      controls.className = 'image-carousel__controls';
      const previous = this.createButton('Previous image', '\u2190', 'image-carousel__arrow image-carousel__arrow--previous');
      const next = this.createButton('Next image', '\u2192', 'image-carousel__arrow image-carousel__arrow--next');
      controls.append(previous, next);
      const pagination = document.createElement('div');
      pagination.className = 'image-carousel__pagination';
      pagination.setAttribute('aria-label', 'Choose an image');
      const dots = definition.images.map((_, index) => {
        const dot = this.createButton(`Show image ${index + 1}`, '', 'image-carousel__dot');
        dot.addEventListener('click', () => show(index));
        pagination.append(dot);
        return dot;
      });
      stage.append(controls);
      carousel.append(stage, pagination);
      strip.append(carousel);

      let current = 0;
      let stackAnimations = [];
      const show = index => {
        const target = (index + definition.images.length) % definition.images.length;
        const animateStack = isStack && !reducedMotion.matches &&
          carousel.dataset.current != null && target !== current;
        const direction = index > current ? 1 : -1;
        stackAnimations.forEach(animation => animation.cancel());
        stackAnimations = [];
        const outgoing = animateStack ? slides[1].cloneNode() : null;
        if (outgoing) {
          outgoing.className = 'image-carousel__slide image-carousel__transition';
          outgoing.setAttribute('aria-hidden', 'true');
          outgoing.style.zIndex = '2';
          stage.append(outgoing);
        }
        current = target;
        carousel.dataset.current = String(current);
        if (isStack) {
          const indices = [current - 1, current, current + 1]
            .map(slideIndex => (slideIndex + definition.images.length) % definition.images.length);
          slides.forEach((image, slot) => {
            const item = definition.images[indices[slot]];
            image.src = item.src;
            image.alt = item.alt || '';
            image.hidden = false;
            image.setAttribute('aria-hidden', slot === 1 ? 'false' : 'true');
            image.classList.toggle('is-previous', slot === 0);
            image.classList.toggle('is-active', slot === 1);
            image.classList.toggle('is-next', slot === 2);
          });
          if (outgoing) {
            const timing = { duration: 600, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
            const mainPosition = 'translateX(0) scale(1)';
            const previewPosition = 'translateX(-50%) scale(.6)';
            const incomingPosition = 'translateX(8%) scale(.84)';
            const exit = outgoing.animate([
              { transform: mainPosition },
              { transform: direction > 0 ? previewPosition : incomingPosition },
            ], timing);
            const enter = slides[1].animate([
              { transform: direction > 0 ? incomingPosition : previewPosition },
              { transform: mainPosition },
            ], timing);
            const preview = slides[0].animate(direction > 0 ? [
              { visibility: 'hidden' },
              { visibility: 'hidden' },
            ] : [
              { transform: 'translateX(-58%) scale(.44)' },
              { transform: previewPosition },
            ], timing);
            stackAnimations = [exit, enter, preview];
            exit.finished.then(() => outgoing.remove(), () => outgoing.remove());
          }
        } else {
          slides.forEach((image, slideIndex) => {
            const item = definition.images[slideIndex];
            image.src = item.src;
            image.alt = item.alt || '';
            image.hidden = slideIndex !== current;
            image.classList.toggle('is-active', slideIndex === current);
          });
        }
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === current);
          dot.setAttribute('aria-current', dotIndex === current ? 'true' : 'false');
        });
        previous.disabled = definition.images.length < 2;
        next.disabled = definition.images.length < 2;
      };
      previous.addEventListener('click', () => show(current - 1));
      next.addEventListener('click', () => show(current + 1));
      reducedMotion.addEventListener('change', () => {
        if (reducedMotion.matches) stackAnimations.forEach(animation => animation.cancel());
      });
      carousel.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.key === 'Home') show(0);
        else if (event.key === 'End') show(slides.length - 1);
        else show(current + (event.key === 'ArrowRight' ? 1 : -1));
      });
      let pointerStart = null;
      stage.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
      });
      stage.addEventListener('pointerup', event => {
        if (!pointerStart || pointerStart.id !== event.pointerId) return;
        const deltaX = event.clientX - pointerStart.x;
        const deltaY = event.clientY - pointerStart.y;
        pointerStart = null;
        if (Math.abs(deltaX) < 36 || Math.abs(deltaX) < Math.abs(deltaY)) return;
        show(current + (deltaX < 0 ? 1 : -1));
      });
      stage.addEventListener('pointercancel', () => { pointerStart = null; });
      carousel.addEventListener('wheel', event => {
        if (!desktop.matches && Math.abs(event.deltaX) > Math.abs(event.deltaY)) event.stopPropagation();
      }, { passive: true });
      show(0);
      entries.push({ definition, page, carousel });
    }

    const update = () => {
      const stripBounds = strip.getBoundingClientRect();
      const viewport = container.getBoundingClientRect();
      const defaultStrength = Number.parseFloat(getComputedStyle(document.documentElement)
        .getPropertyValue('--image-parallax-amount')) || 0;
      for (const { definition, page, carousel } of entries) {
        const settings = desktop.matches ? definition : { ...definition, ...definition.mobile };
        const bounds = (page.querySelector('.filmstrip-stage') || page).getBoundingClientRect();
        const percent = (value, fallback) => Number.parseFloat(value ?? fallback) / 100;
        const width = bounds.width * percent(settings.width, 70);
        const useRight = settings.right != null && !( !desktop.matches && definition.mobile?.left != null);
        const useBottom = settings.bottom != null && !( !desktop.matches && definition.mobile?.top != null);
        carousel.style.width = `${width}px`;
        carousel.style.left = `${bounds.left - stripBounds.left + (useRight
          ? bounds.width * (1 - percent(settings.right, 0)) - width
          : bounds.width * percent(settings.left, 12))}px`;
        carousel.style.top = `${bounds.top - stripBounds.top + bounds.height * (useBottom
          ? 1 - percent(settings.bottom, 0) : percent(settings.top, 12))}px`;
        carousel.style.transform = useBottom ? 'translateY(-100%)' : 'none';
        const distance = desktop.matches ? viewport.left - bounds.left : viewport.top - bounds.top;
        const override = settings.parallax == null ? NaN : Number(settings.parallax);
        const displacement = distance * (Number.isFinite(override) ? override : defaultStrength);
        const offset = reducedMotion.matches ? 0 : Number.isFinite(override)
          ? displacement : Math.max(-80, Math.min(80, displacement));
        setParallaxTranslation(carousel, offset, desktop.matches, reducedMotion.matches);
      }
    };
    this.update = update;
    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    desktop.addEventListener('change', update);
    reducedMotion.addEventListener('change', update);
    const observer = new ResizeObserver(update);
    observer.observe(container);
    for (const { page } of entries) observer.observe(page);
    update();
  },

  createButton(label, text, className) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.setAttribute('aria-label', label);
    button.textContent = text;
    return button;
  },
};

export default imageCarouselModule;